import { useCallback, useEffect, useRef, useState } from 'react';
import { toUserMessage } from '@platform/api-client';
import { authApi } from '@/shared/api/auth.api';
import type { PhoneVerifyFieldProps } from '@/features/auth/types/phone-verify-field.type.ts';

const OTP_SECONDS = 300;
const onlyDigits = (v: string) => v.replace(/\D/g, '').slice(0, 11);

/**
 * 휴대폰 SMS OTP 인증 필드 (솔라피 연동).
 *
 * 흐름: 번호 입력 → 인증번호 발송 → 코드 입력 → 확인 → onVerified().
 * 인증 성공 시 서버가 번호 기준 proof 를 저장하고, 부모(가입/번호변경)는
 * 같은 번호를 제출해 proof 를 소비한다. 가입/번호변경은 인증 완료가 필수다.
 */
export function PhoneVerifyField({
  phone,
  onPhoneChange,
  verified,
  onVerified,
}: PhoneVerifyFieldProps) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (seconds <= 0) return;
    timer.current = window.setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [seconds]);

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

  const handleSend = useCallback(async () => {
    setError(undefined);
    setNotice(undefined);
    if (phone.replace(/\D/g, '').length < 10) {
      setError('휴대폰 번호를 정확히 입력해 주세요.');
      return;
    }
    setSending(true);
    try {
      await authApi.sendPhoneOtp(phone);
      setSent(true);
      setSeconds(OTP_SECONDS);
      setNotice('인증번호를 발송했습니다.');
    } catch (e) {
      setError(toUserMessage(e, '인증번호 발송에 실패했습니다. 잠시 후 다시 시도해 주세요.'));
    } finally {
      setSending(false);
    }
  }, [phone]);

  const handleVerify = useCallback(async () => {
    setError(undefined);
    setVerifying(true);
    try {
      await authApi.verifyPhoneOtp(phone, code);
      onVerified();
      setSeconds(0);
      setNotice('인증이 완료되었습니다.');
    } catch (e) {
      setError(toUserMessage(e, '인증번호가 올바르지 않거나 만료되었습니다.'));
    } finally {
      setVerifying(false);
    }
  }, [phone, code, onVerified]);

  return (
    <div className="space-y-2">
      <span className="text-sm text-adobe-muted">
        휴대폰 인증 <span className="text-danger">*</span>
      </span>

      <div className="flex gap-2">
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          placeholder="01012345678"
          value={phone}
          disabled={verified}
          onChange={(e) => onPhoneChange(onlyDigits(e.target.value))}
          className="block w-full rounded border border-gray-300 px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:bg-gray-100"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={sending || verified}
          className="shrink-0 whitespace-nowrap rounded border border-brand-500 px-3 text-[13px] font-semibold text-brand-600 transition-colors hover:bg-brand-50 disabled:opacity-50"
        >
          {verified ? '인증 완료' : sent ? '재발송' : sending ? '발송 중…' : '인증번호 발송'}
        </button>
      </div>

      {sent && !verified && (
        <div className="flex gap-2">
          <div className="relative w-full">
            <input
              type="text"
              inputMode="numeric"
              placeholder="인증번호 6자리"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="block w-full rounded border border-gray-300 px-3 py-2 pr-14 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            />
            {seconds > 0 && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] font-medium text-danger">
                {mmss}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleVerify}
            disabled={verifying || code.length < 4}
            className="shrink-0 rounded bg-neutral-950 px-4 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {verifying ? '확인 중…' : '확인'}
          </button>
        </div>
      )}

      {verified && (
        <p className="text-xs font-semibold text-emerald-600">✓ 휴대폰 인증 완료</p>
      )}
      {error && <p className="text-xs text-danger" role="alert">{error}</p>}
      {notice && !error && <p className="text-xs text-brand-600">{notice}</p>}
    </div>
  );
}
